/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, Flex, Grid } from 'theme-ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import PageWrapper from '../../../containers/MainLayout';
import store from 'redux/store';
import CategoryLabel from 'components/CategoryLabel';
import { selectCategory } from 'reducers/category/selectors';
import { fetchCategory, setCategory } from 'reducers/category/actions';
import PostCard from 'components/PostCard';
import UserAvatars from 'components/common/UserAvatars';
import SidebarCategories from 'components/Sidebar/SidebarCategories';
import LikeButton from 'components/common/LikeButton';
import { useSelector, useDispatch } from 'react-redux';
import { setAuthModal } from 'reducers/auth/actions';
import {
  selectCurrentUser,
  selectCategoryLiked,
} from 'reducers/currentUser/selectors';
import { likeCategory, fetchCategoryLikes } from 'reducers/category/actions';

SingleCategoryPage.getInitialProps = async ({ query }) => {
  const { slug } = query;
  try {
    const data = await fetchCategory({ slug })(store.dispatch);
    return {
      data,
    };
  } catch (error) {
    return {
      data: {},
      error,
    };
  }
};

export default function SingleCategoryPage({ data, ...props }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { category, categoryLoading, categoryPosts, categoryLikes } =
    useSelector(selectCategory);
  const { query } = useRouter();

  const categoryData = category?.en ? category : data?.category || {};

  const { currentUser } = useSelector(selectCurrentUser);
  const likes = useSelector(selectCategoryLiked);

  const handleLike = (e) => {
    e.stopPropagation();
    if (!currentUser?.firstName) {
      dispatch(setAuthModal('login'));
      return;
    }
    likeCategory({
      slug: category?.slug,
      liked: !!likes[category?.id],
      payload: {
        createdAt: new Date(),
        categoryId: category?.id.toString(),
        userId: currentUser?.id,
        user: {
          avatarUrl: currentUser?.avatarUrl,
          username: currentUser?.username,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
        },
      },
    })(dispatch);
  };

  useEffect(() => {
    if (query.slug !== data?.category?.slug) {
      fetchCategory({ slug: query.slug as string })(dispatch);
      fetchCategoryLikes({ slug: query.slug as string })(dispatch);
    } else {
      dispatch(setCategory(data));
    }
  }, [query]);

  const renderPosts = () => {
    return (
      <Grid columns={[1]}>
        {categoryPosts?.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </Grid>
    );
  };

  return (
    <PageWrapper
      {...props}
      title={`Category - ${categoryData?.en}`}
      url={`/categories/${categoryData?.slug}`}
      image={categoryData?.coverImageUrl}
      description={categoryData.en || categoryData.fr}
      bodySx={{
        variant: 'container.box',
        bg: 'textWhite',
        p: 3,
        borderRadius: 5,
      }}
    >
      <Flex
        sx={{
          height: '20vh',
          minHeight: ['150px', '200px', '300px'],
          backgroundImage: `linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url("${categoryData?.coverImageUrl}");`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: 'rgba(0, 0, 0, 0.075)',
          boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.09)',
          p: 3,
          mb: 3,
          borderRadius: 5,
        }}
      >
        <div>
          <CategoryLabel slug={categoryData?.slug}>
            {categoryData?.en}
          </CategoryLabel>
        </div>
        <Flex sx={{ justifyContent: 'flex-end' }}>
          <UserAvatars users={categoryLikes} count={category?.likesCount} />
          <LikeButton
            liked={likes[category?.id]}
            onClick={handleLike}
            loading={!category?.slug}
            width="50px"
            height="50px"
            ml="15px"
          />
        </Flex>
      </Flex>

      <Grid columns={[1, '1.5fr 1fr']}>
        <div>{renderPosts()}</div>
        <div>
          <SidebarCategories limit={9} />
        </div>
      </Grid>
    </PageWrapper>
  );
}
